import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ContentPageTheme, Form, Player, ReviewWidgetConfig, ReviewWidgetConfigServices } from "@/services/bedrock";
import { publicWebOrigin } from "@/utils";
import { autoResizeSnippet } from "../../components/EmbedSnippet";
import { findTheme } from "../../components/sampleReviews";
import { useIframePreview } from "../../components/useIframePreview";
import { PLAYER_ID_PLACEHOLDER, usePlayerPreviewLoading } from "../../components/usePreviewPlayer";
import { buildReviewWidgetPreviewData } from "./reviewWidgetPreviewData";

const EMBED_IFRAME_HEIGHT = 480;

interface UseReviewWidgetPreviewArgs {
  config: ReviewWidgetConfig;
  themes: ContentPageTheme[];
  forms: Form[];
  previewPlayer: Player | null;
}

const useReviewWidgetPreview = ({ config, themes, forms, previewPlayer }: UseReviewWidgetPreviewArgs) => {
  const [includeAutoResize, setIncludeAutoResize] = useState(true);
  const iframeOrigin = publicWebOrigin();
  const isSamplePreview = !previewPlayer?.guid;
  const iframeSrc = `${iframeOrigin}/embed/review/preview`;
  const previewPlayerName = previewPlayer?.fullName || undefined;

  const previewOverrides = useMemo(
    () => ({
      showStarSummary: config.showStarSummary,
      showSentimentSummary: config.showSentimentSummary,
      showNpsSummary: config.showNpsSummary,
      formId: config.formId ?? null,
      contentPageThemeId: config.contentPageThemeId ?? null,
      reviewSortType: config.reviewSortType,
      useCondensedQuotes: config.useCondensedQuotes,
      approvedForSocialOnly: config.approvedForSocialOnly,
      autoScrollEnabled: config.autoScrollEnabled,
    }),
    [config]
  );

  const {
    data: playerPreviewData,
    isFetching: isFetchingPlayerPreview,
    isError: isPreviewError,
    error: previewError,
  } = useQuery({
    queryKey: [
      "reviewWidgetPublicPreview",
      config.slug,
      previewPlayer?.guid,
      previewOverrides.formId,
      previewOverrides.contentPageThemeId,
      previewOverrides.reviewSortType,
      previewOverrides.approvedForSocialOnly,
      previewOverrides.showStarSummary,
      previewOverrides.showSentimentSummary,
      previewOverrides.showNpsSummary,
    ],
    queryFn: () => ReviewWidgetConfigServices.getPublicPreview(config.slug, previewPlayer!.guid, previewOverrides),
    enabled: !!config.slug && !!previewPlayer?.guid,
    placeholderData: previousData => previousData,
  });

  const isLoading = usePlayerPreviewLoading({
    playerGuid: previewPlayer?.guid,
    isFetching: isFetchingPlayerPreview,
    isError: isPreviewError,
    error: previewError,
  });

  const previewData = useMemo(() => {
    if (previewPlayer?.guid) {
      if (isPreviewError || !playerPreviewData) return null;

      const { formId, contentPageThemeId, showStarSummary, showSentimentSummary, showNpsSummary, ...configOverrides } =
        previewOverrides;
      const selectedTheme = findTheme(themes, contentPageThemeId);
      const selectedForm = formId ? forms.find(form => form.id === formId) : null;
      const summary = {
        ...(showStarSummary ? { star: playerPreviewData.summary?.star } : {}),
        ...(showSentimentSummary ? { sentiment: playerPreviewData.summary?.sentiment } : {}),
        ...(showNpsSummary ? { nps: playerPreviewData.summary?.nps } : {}),
      };

      return {
        ...playerPreviewData,
        ...configOverrides,
        summary: Object.keys(summary).length > 0 ? summary : null,
        ...(selectedTheme ? { theme: selectedTheme } : {}),
        ...(selectedForm
          ? { formUrl: playerPreviewData.formUrl ?? selectedForm.testRequest?.url ?? "#write-a-review" }
          : {}),
      };
    }
    return buildReviewWidgetPreviewData(config, themes);
  }, [config, themes, forms, previewPlayer?.guid, playerPreviewData, previewOverrides, isPreviewError]);

  const embedUrl = useMemo(() => {
    if (!config.slug) return "";
    const player = previewPlayer?.guid ? encodeURIComponent(previewPlayer.guid) : PLAYER_ID_PLACEHOLDER;
    return `${iframeOrigin}/embed/review/${config.slug}?player=${player}`;
  }, [config.slug, iframeOrigin, previewPlayer?.guid]);

  const iframeSnippet = useMemo(() => {
    if (!embedUrl) return "";

    if (!includeAutoResize) {
      return `<iframe src="${embedUrl}" width="100%" height="${EMBED_IFRAME_HEIGHT}"></iframe>`;
    }

    return autoResizeSnippet(embedUrl, EMBED_IFRAME_HEIGHT, "sc-reputation-embed", "REVIEW_WIDGET_HEIGHT");
  }, [embedUrl, includeAutoResize]);

  const message = useMemo(() => ({ type: "REVIEW_WIDGET", payload: previewData }), [previewData]);
  const { iframeRef, iframeHeight } = useIframePreview({
    src: iframeSrc,
    message,
    heightMessageType: "REVIEW_WIDGET_HEIGHT",
    defaultHeight: EMBED_IFRAME_HEIGHT,
  });

  return {
    controlsProps: {
      includeAutoResize,
      onIncludeAutoResizeChange: setIncludeAutoResize,
      embedUrl,
      isSamplePreview,
    },
    previewProps: {
      iframeRef,
      iframeSrc,
      includeAutoResize,
      iframeHeight,
      isLoading,
      previewPlayerName,
      iframeSnippet,
    },
  };
};

export { useReviewWidgetPreview, EMBED_IFRAME_HEIGHT };
